/**
 * Settings Dialog
 *
 * Opened from the sidebar profile dropdown. Contains five tabs:
 * - Profile: Avatar upload/delete, fallback avatar color
 * - Preferences: Theme, notifications, AI coaching style
 * - Household: Member list with split date management and member removal
 * - Institutions: Bank connection health and sync controls
 * - Account: Account deletion
 */

import { useAuth } from '@/features/auth/hooks/use-auth';
import {
  InlineColorPicker,
} from '@/features/categories/components/color-picker';
import { Icon, IconKey } from '@/shared/components/icon';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Separator } from '@/shared/components/ui/separator';
import { Switch } from '@/shared/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { PREF_KEYS } from '@/shared/constants';
import { toast } from '@/shared/hooks/use-toast';
import { useDeleteAvatar, useUpdateAvatarColor, useUploadAvatar } from '@/shared/hooks/use-user';
import {
  useAiPreferences,
  useNotificationPreferences,
  useUpdatePreference,
} from '@/shared/hooks/use-user-preferences';
import { exportService } from '@/shared/services/export-service';
import type { CoachingStyle, Theme } from '@/shared/types/preferences';
import { cn } from '@/shared/utils';
import { useRef, useState } from 'react';
import { useTheme } from '../context/theme-provider';
import { DeleteAccountSection } from './delete-account-section';
import { HouseholdTab } from './household-tab';
import { InstitutionsTab } from './institutions-tab';

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: string;
}

// ---------------------------------------------------------------------------
// Theme options
// ---------------------------------------------------------------------------

const THEME_OPTIONS: { value: Theme; label: string; icon: IconKey }[] = [
  { value: 'light', label: 'Light', icon: IconKey.Sun },
  { value: 'dark', label: 'Dark', icon: IconKey.Moon },
  { value: 'system', label: 'System', icon: IconKey.Monitor },
];

// ---------------------------------------------------------------------------
// Coaching styles
// ---------------------------------------------------------------------------

const COACHING_STYLES: { value: CoachingStyle; label: string; description: string }[] = [
  { value: 'encouraging', label: 'Encouraging', description: 'Celebrates wins and gently nudges toward goals' },
  { value: 'direct', label: 'Direct', description: 'Gives it to you straight, no fluff' },
  { value: 'analytical', label: 'Analytical', description: 'Data-driven insights and detailed breakdowns' },
];

export function SettingsDialog({ open, onOpenChange, defaultTab }: SettingsDialogProps) {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { theme, setTheme } = useTheme();

  const uploadAvatar = useUploadAvatar();
  const deleteAvatar = useDeleteAvatar();
  const updateAvatarColor = useUpdateAvatarColor();

  // Preference hooks
  const updateThemePref = useUpdatePreference(PREF_KEYS.THEME);
  const notifications = useNotificationPreferences();
  const updateNotifications = useUpdatePreference(PREF_KEYS.NOTIFICATIONS);
  const aiPrefs = useAiPreferences();
  const updateAi = useUpdatePreference(PREF_KEYS.AI);

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportService.exportHouseholdData();
    } catch (error) {
      toast({
        title: 'Failed to export data',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const initials = user?.name?.charAt(0).toUpperCase() ?? '?';
  const hasPhoto = !!user?.avatar_url;
  const isUploading = uploadAvatar.isPending;
  const isDeleting = deleteAvatar.isPending;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    uploadAvatar.mutate(file, {
      onError: (error) => {
        toast({
          title: 'Failed to upload avatar',
          description: error instanceof Error ? error.message : 'Please try again.',
          variant: 'destructive',
        });
      },
    });
  };

  const handleRemovePhoto = () => {
    deleteAvatar.mutate(undefined, {
      onError: (error) => {
        toast({
          title: 'Failed to remove avatar',
          description: error instanceof Error ? error.message : 'Please try again.',
          variant: 'destructive',
        });
      },
    });
  };

  const handleColorChange = (color: string) => {
    updateAvatarColor.mutate(color, {
      onError: () => {
        toast({ title: 'Failed to update avatar color', variant: 'destructive' });
      },
    });
  };

  const handleThemeChange = (value: Theme) => {
    setTheme(value);
    updateThemePref(value);
  };

  const handleNotificationToggle = (key: keyof typeof notifications, checked: boolean) => {
    updateNotifications({ ...notifications, [key]: checked });
  };

  const handleCoachingStyleChange = (style: CoachingStyle) => {
    updateAi({ ...aiPrefs, coaching_style: style });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue={defaultTab ?? "profile"}>
          <TabsList className="w-full">
            <TabsTrigger value="profile" className="flex-1">Profile</TabsTrigger>
            <TabsTrigger value="preferences" className="flex-1">Preferences</TabsTrigger>
            <TabsTrigger value="household" className="flex-1">Household</TabsTrigger>
            <TabsTrigger value="institutions" className="flex-1">Institutions</TabsTrigger>
            <TabsTrigger value="account" className="flex-1">Account</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className='mt-6'>
            <div className="flex flex-col items-center gap-4 py-2">
              {/* Avatar preview with camera overlay */}
              <button
                type="button"
                className="relative group rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                <Avatar className="h-24 w-24">
                  {user?.avatar_url && (
                    <AvatarImage src={user.avatar_url} alt={user?.name} />
                  )}
                  <AvatarFallback
                    style={user?.avatar_color ? { backgroundColor: user.avatar_color } : undefined}
                    className="text-white text-2xl font-medium"
                  >
                    {initials}
                  </AvatarFallback>
                </Avatar>

                {/* Camera overlay on hover */}
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {isUploading ? (
                    <Icon icon={IconKey.Loading} size={24} className="text-white animate-spin" />
                  ) : (
                    <Icon icon={IconKey.Camera} size={24} className="text-white" />
                  )}
                </div>
              </button>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileChange}
                className="hidden"
                disabled={isUploading}
              />

              <p className="text-xs text-muted-foreground">
                JPEG, PNG, GIF, or WebP. Max 2 MB.
              </p>

              {/* Remove photo button */}
              {hasPhoto && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemovePhoto}
                  disabled={isDeleting}
                  className="text-destructive hover:text-destructive"
                >
                  {isDeleting ? (
                    <Icon icon={IconKey.Loading} size={16} className="animate-spin mr-1" />
                  ) : (
                    <Icon icon={IconKey.Trash} size={16} className="mr-1" />
                  )}
                  Remove photo
                </Button>
              )}
            </div>

            {/* Fallback color picker */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-text">Avatar color</label>
              <InlineColorPicker
                value={user?.avatar_color}
                onChange={handleColorChange}
              />
            </div>

            <Separator />

            {/* Data export */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Export Your Data</h4>
              <p className="text-sm text-muted-foreground">
                Download a ZIP file containing your transactions, accounts,
                budgets, goals, and recurring transactions as CSV files.
              </p>
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? (
                  <>
                    <Icon icon={IconKey.Loading} size={16} className="mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  'Download Export'
                )}
              </Button>
            </div>
          </TabsContent>

          {/* ── Preferences Tab ────────────────────────────────────── */}
          <TabsContent value="preferences" className="mt-6 space-y-6">
            {/* Appearance */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-text">Appearance</h3>
              <div className="flex gap-2">
                {THEME_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleThemeChange(opt.value)}
                    className={cn(
                      'flex-1 flex flex-col items-center gap-1.5 rounded-md border p-3 text-sm transition-colors hover:bg-muted/50',
                      theme === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border',
                    )}
                  >
                    <Icon icon={opt.icon} size={18} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            {/* Notifications */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-text">Notifications</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="settlement-reminders">Settlement reminders</Label>
                    <p className="text-xs text-muted-foreground">Get reminded about unsettled expenses</p>
                  </div>
                  <Switch
                    id="settlement-reminders"
                    checked={notifications.settlement_reminders}
                    onCheckedChange={(checked) => handleNotificationToggle('settlement_reminders', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="budget-alerts">Budget alerts</Label>
                    <p className="text-xs text-muted-foreground">Notified when nearing budget limits</p>
                  </div>
                  <Switch
                    id="budget-alerts"
                    checked={notifications.budget_alerts}
                    onCheckedChange={(checked) => handleNotificationToggle('budget_alerts', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="goal-milestones">Goal milestones</Label>
                    <p className="text-xs text-muted-foreground">Celebrate when you hit savings milestones</p>
                  </div>
                  <Switch
                    id="goal-milestones"
                    checked={notifications.goal_milestones}
                    onCheckedChange={(checked) => handleNotificationToggle('goal_milestones', checked)}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* AI Assistant */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-text">AI Assistant</h3>
                <Badge variant="secondary" className="text-[10px]">Coming soon</Badge>
              </div>
              <p className="text-xs text-muted-foreground">Choose how your AI coach communicates with you</p>
              <div className="grid gap-2">
                {COACHING_STYLES.map((style) => (
                  <button
                    key={style.value}
                    type="button"
                    onClick={() => handleCoachingStyleChange(style.value)}
                    className={cn(
                      'flex flex-col items-start rounded-md border p-3 text-left transition-colors hover:bg-muted/50',
                      aiPrefs.coaching_style === style.value
                        ? 'border-primary'
                        : 'border-border',
                    )}
                  >
                    <span className="text-sm font-medium">{style.label}</span>
                    <span className="text-xs text-muted-foreground">{style.description}</span>
                  </button>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="household">
            <HouseholdTab />
          </TabsContent>

          <TabsContent value="institutions">
            <InstitutionsTab />
          </TabsContent>

          <TabsContent value="account">
            <DeleteAccountSection />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
